/**
 * Sentry configuration for Scribe frontend
 *
 * Handles initialization of Sentry SDK with:
 * - Data scrubbing for sensitive information (PHI/PII)
 * - Performance monitoring and session replay
 * - User context tracking (non-PII only)
 *
 * PHI scrubbing logic mirrors @careflux/utils/sentry patterns.
 */

import * as Sentry from '@sentry/react';

const SCRUB_KEYS = [
  // Chat/message content
  'title',
  'message_text',
  'messageText',
  'message',
  'text',
  'preview',
  'search',
  'content',
  'body',
  'complaint',
  // Medical data (PHI)
  'dosage',
  'instructions',
  'medication_name',
  'prescriber_name',
  'prescription',
  'diagnosis',
  'symptoms',
  'medical_history',
  'notes',
  'assessment',
  // Patient/User PII
  'email',
  'phone',
  'address',
  'national_id',
  'ssn',
  'kennitala',
  'kenni_id',
  'patient_id',
  'name',
  'patient_name',
  'first_name',
  'middle_name',
  'last_name',
  'username',
  'user_name',
  'full_name',
  // Authentication
  'password',
  'token',
  'api_key',
  'session',
  'csrf_token',
  'access_token',
  'refresh_token',
  // Generic sensitive fields
  'data',
  'error',
];

const SCRUB_KEYS_LOWER = SCRUB_KEYS.map((key) => key.toLowerCase());

let isSentryInitialized = false;

const FEEDBACK_WIDGET_ID = 'sentry-feedback';
const FEEDBACK_HELP_LINK_ATTRIBUTE = 'data-careflux-feedback-help-link';
const FEEDBACK_EMAIL_INPUT_SELECTOR = 'input[name="email"]';
// Only used when index.css has not defined --primary (e.g. styles failed to load).
const FALLBACK_PRIMARY = 'oklch(0.55 0.2 250)';
const FALLBACK_PRIMARY_FOREGROUND = '#ffffff';

export const readThemeColor = (token: string, fallback: string): string =>
  getComputedStyle(document.documentElement).getPropertyValue(token).trim() || fallback;

// Contact details for the feedback form only. Deliberately kept out of the
// Sentry user scope so they are never attached to error, trace or replay
// events — they leave the browser only when the clinician submits the form.
let feedbackContactEmail: string | null = null;
let feedbackContactName: string | null = null;

type FeedbackContact = {
  email?: string | null;
  name?: string | null;
};

// The widget has no name input (showName: false), so the name would always
// reach Sentry empty. Fill it on the way out instead — this hook only sees
// feedback events, so the name never reaches any other event type.
const attachFeedbackContactName = (): void => {
  Sentry.getClient()?.on('beforeSendFeedback', (event) => {
    const feedback = event.contexts?.feedback;
    if (feedback && !feedback.name && feedbackContactName) {
      feedback.name = feedbackContactName;
    }
  });
};

export const prefillFeedbackEmail = (): void => {
  if (!feedbackContactEmail || typeof document === 'undefined') {
    return;
  }

  const shadowRoot = document.getElementById(FEEDBACK_WIDGET_ID)?.shadowRoot;
  const input = shadowRoot?.querySelector<HTMLInputElement>(FEEDBACK_EMAIL_INPUT_SELECTOR);
  if (!input || input.value) {
    return;
  }

  input.value = feedbackContactEmail;
  input.dispatchEvent(new Event('input', { bubbles: true }));
};

export const addFeedbackHelpLink = (): void => {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return;
  }

  const shadowRoot = document.getElementById(FEEDBACK_WIDGET_ID)?.shadowRoot;
  const formTop = shadowRoot?.querySelector<HTMLElement>('.form__top');
  if (!shadowRoot || !formTop || shadowRoot.querySelector(`[${FEEDBACK_HELP_LINK_ATTRIBUTE}="true"]`)) {
    return;
  }

  const link = document.createElement('a');
  link.setAttribute(FEEDBACK_HELP_LINK_ATTRIBUTE, 'true');
  link.href = new URL('/faqs', window.location.origin).toString();
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  link.textContent = 'Skoða hjálparmiðstöðina áður en þú sendir skilaboð';
  link.style.alignItems = 'center';
  link.style.color = readThemeColor('--primary', FALLBACK_PRIMARY);
  link.style.display = 'inline-flex';
  link.style.fontSize = '14px';
  link.style.fontWeight = '600';
  link.style.lineHeight = '1.4';
  link.style.marginBottom = '4px';
  link.style.textDecoration = 'underline';
  link.style.textUnderlineOffset = '3px';

  formTop.prepend(link);
};

const scrubSensitiveData = (data: unknown, depth = 0): unknown => {
  if (depth > 10) return '[Max Depth Reached]';
  if (data === null || data === undefined) return data;

  if (Array.isArray(data)) {
    return data.map((item) => scrubSensitiveData(item, depth + 1));
  }

  if (typeof data === 'object') {
    const source = data as Record<string, unknown>;
    const scrubbed: Record<string, unknown> = {};

    for (const key in source) {
      if (!Object.prototype.hasOwnProperty.call(source, key)) continue;

      const lowerKey = key.toLowerCase();
      let shouldScrub = false;
      for (const scrubKey of SCRUB_KEYS_LOWER) {
        if (lowerKey.includes(scrubKey)) {
          shouldScrub = true;
          break;
        }
      }

      if (shouldScrub) {
        scrubbed[key] = '[Filtered]';
        continue;
      }

      scrubbed[key] = scrubSensitiveData(source[key], depth + 1);
    }

    return scrubbed;
  }

  return data;
};

export const initScribeSentry = (): boolean => {
  if (isSentryInitialized) return false;

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    console.warn('Sentry DSN not provided - Sentry monitoring disabled');
    return false;
  }

  const environment = import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE || 'development';

  Sentry.init({
    dsn,
    environment,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        networkDetailAllowUrls: [window.location.origin],
        networkCaptureBodies: false,
        networkRequestHeaders: ['Content-Type', 'X-Request-ID'],
        networkResponseHeaders: ['Content-Type', 'X-Request-ID'],
      }),
    ],

    tracesSampleRate: environment === 'production' ? 0.2 : 1.0,
    replaysSessionSampleRate: environment === 'production' ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,

    beforeSend(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
      if (event.request) {
        event.request = scrubSensitiveData(event.request) as typeof event.request;
      }
      if (event.contexts) {
        event.contexts = scrubSensitiveData(event.contexts) as typeof event.contexts;
      }
      if (event.extra) {
        event.extra = scrubSensitiveData(event.extra) as typeof event.extra;
      }
      if (event.user) {
        const userId = event.user.id;
        event.user = userId ? { id: userId } : {};
      }
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => ({
          ...breadcrumb,
          data: breadcrumb.data ? (scrubSensitiveData(breadcrumb.data) as typeof breadcrumb.data) : undefined,
          message:
            breadcrumb.message?.includes('password') || breadcrumb.message?.includes('token')
              ? '[Filtered]'
              : breadcrumb.message,
        }));
      }
      return event;
    },

    sendDefaultPii: false,

    ignoreErrors: [
      'top.GLOBALS',
      'Network request failed',
      'NetworkError',
      'Failed to fetch',
      'AbortError',
      'User aborted',
    ],

    denyUrls: [/extensions\//i, /^chrome:\/\//i],
  });

  attachFeedbackContactName();

  const primary = readThemeColor('--primary', FALLBACK_PRIMARY);

  // Lazy-load feedback integration to keep it out of the main bundle
  Sentry.lazyLoadIntegration('feedbackIntegration')
    .then((feedbackIntegration) => {
      Sentry.addIntegration(
        feedbackIntegration({
          autoInject: false,
          showName: false,
          showEmail: true,
          isEmailRequired: true,
          enableScreenshot: true,
          showBranding: false,
          colorScheme: 'light',

          // Icelandic text
          formTitle: 'Hvernig getum við aðstoðað?',
          submitButtonLabel: 'Senda',
          cancelButtonLabel: 'Hætta við',
          emailLabel: 'Netfang',
          emailPlaceholder: 'nafn@netfang.is',
          messageLabel: 'Lýsing',
          messagePlaceholder: 'Lýsið vandamálinu — ekki setja inn persónuupplýsingar sjúklinga',
          addScreenshotButtonLabel: 'Bæta við skjámynd',
          removeScreenshotButtonLabel: 'Fjarlægja skjámynd',
          successMessageText: 'Takk! Við munum skoða þetta.',
          isRequiredLabel: '(nauðsynlegt)',
          onFormOpen: () => {
            addFeedbackHelpLink();
            prefillFeedbackEmail();
          },

          // Careflux theme (light only)
          themeLight: {
            accentBackground: primary,
            accentForeground: readThemeColor('--primary-foreground', FALLBACK_PRIMARY_FOREGROUND),
            background: '#ffffff',
            foreground: '#0f172a',
            successColor: '#16a34a',
            errorColor: '#dc2626',
            outline: `1px auto ${primary}`,
            fontFamily: "'Inter', sans-serif",
          },
        })
      );
    })
    .catch(() => {
      // Feedback widget failed to load — non-critical, app continues normally
    });

  isSentryInitialized = true;
  return true;
};

export const setSentryUser = (userId: string | number | null, contact?: FeedbackContact): void => {
  feedbackContactEmail = contact?.email?.trim() || null;
  feedbackContactName = contact?.name?.trim() || null;

  if (userId) {
    Sentry.setUser({ id: String(userId) });
  } else {
    Sentry.setUser(null);
  }
};

export const clearSentryUser = (): void => {
  feedbackContactEmail = null;
  feedbackContactName = null;
  Sentry.setUser(null);
};

export { Sentry };
